import {
  faArrowRight,
  faChevronLeft,
  faChevronRight,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useReviewAnalysisDateRange } from 'src/hooks/useReviewAnalysisDateRange'
import { useGetReviewAnalysisGuestReviewsQuery } from 'src/reactQuery/useGetReviewAnalysisGuestReviewsQuery'
import {
  FacilityTab,
  GuestReview,
  ReviewPlatform,
} from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './guestReviewsOverview.module.scss'
import { getPlatformIcon } from '../../platformIcons'
import { ScopeSelect } from '../../ScopeSelect'

const PLATFORM_LABELS: Record<ReviewPlatform, string> = {
  appstore: 'App Store',
  booking: 'Booking.com',
  expedia: 'Expedia',
  google: 'Google Maps',
  playstore: 'Google Play',
  tripadvisor: 'Tripadvisor',
}

const monthLabel = (date?: string) =>
  date
    ? new Date(date).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      })
    : ''

// Five bubbles, Tripadvisor style: filled up to the rounded rating.
const RatingDots: FC<{ value: number }> = ({ value }) => (
  <span aria-label={`${value} of 5`} className={styles.dots}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span
        className={`${styles.dot} ${n <= Math.round(value) ? styles.dotFilled : ''}`}
        key={n}
      />
    ))}
  </span>
)

const Avatar: FC<{ name?: string; src?: string }> = ({ name, src }) => {
  const [failed, setFailed] = useState(false)
  if (src && !failed) {
    return (
      <img
        alt=""
        className={styles.avatar}
        onError={() => setFailed(true)}
        src={src}
      />
    )
  }
  const initials = (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
  return <span className={styles.avatarInitials}>{initials}</span>
}

const GuestReviewCard: FC<{ review: GuestReview }> = ({ review }) => {
  const PlatformIcon = getPlatformIcon(review.platform)
  const platformLabel = PLATFORM_LABELS[review.platform] ?? review.platform
  const text = review.translated || review.text

  return (
    <article className={styles.card}>
      <header className={styles.cardHeader}>
        <span className={styles.platform}>
          <PlatformIcon className={styles.platformIcon} />
          {platformLabel}
        </span>
        {typeof review.rating === 'number' && (
          <span className={styles.score}>
            <span className={styles.scoreValue}>
              {review.rating.toFixed(1)}
            </span>
            <RatingDots value={review.rating} />
            <span className={styles.scoreDate}>{monthLabel(review.date)}</span>
          </span>
        )}
      </header>

      <div className={styles.reviewer}>
        <Avatar name={review.reviewerName} src={review.reviewerAvatar} />
        <span className={styles.reviewerName}>
          {review.reviewerName || 'Guest'}
        </span>
      </div>

      {review.title && <h4 className={styles.reviewTitle}>{review.title}</h4>}
      {text && <p className={styles.reviewText}>{text}</p>}

      {review.tags.length > 0 && (
        <div className={styles.tags}>
          {review.tags.map((tag) => (
            <span className={styles.tag} key={tag}>
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className={styles.ratings}>
        <h5 className={styles.ratingsTitle}>Ratings</h5>
        {review.subRatings.map((sub) => (
          <div className={styles.ratingRow} key={sub.name}>
            <span className={styles.ratingName}>{sub.name}</span>
            <RatingDots value={sub.rating} />
            <span className={styles.ratingValue}>{sub.rating.toFixed(1)}</span>
          </div>
        ))}
      </div>

      <footer className={styles.cardFooter}>
        {review.url ? (
          <a
            className={styles.viewLink}
            href={review.url}
            rel="noopener noreferrer"
            target="_blank"
          >
            View on {platformLabel}
            <FontAwesomeIcon icon={faArrowRight} />
          </a>
        ) : (
          <span className={styles.viewLinkDisabled}>
            {review.venueName || platformLabel}
          </span>
        )}
      </footer>
    </article>
  )
}

interface GuestReviewsOverviewProps {
  // The platform page's facility tabs (`id: null` = All Facilities).
  facilityTabs?: FacilityTab[]
}

export const GuestReviewsOverview: FC<GuestReviewsOverviewProps> = ({
  facilityTabs = [],
}) => {
  const { campaignId = '', platformId = '' } = useParams<{
    campaignId: string
    platformId: string
  }>()
  const dateRange = useReviewAnalysisDateRange()
  const [facilityIds, setFacilityIds] = useState<string[]>([])
  const facilities = facilityTabs
    .filter((tab) => !!tab.id)
    .map((tab) => ({ id: tab.id as string, name: tab.name }))

  useEffect(() => {
    setFacilityIds([])
  }, [platformId])

  const { data } = useGetReviewAnalysisGuestReviewsQuery(
    campaignId,
    platformId,
    facilityIds,
    dateRange
  )

  const trackRef = useRef<HTMLDivElement>(null)
  const [scroll, setScroll] = useState({ left: 0, max: 0, ratio: 1 })

  const measure = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    setScroll({
      left: el.scrollLeft,
      max: Math.max(0, el.scrollWidth - el.clientWidth),
      ratio: el.scrollWidth ? el.clientWidth / el.scrollWidth : 1,
    })
  }, [])

  useEffect(() => {
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure, data])

  const step = (direction: 1 | -1) => {
    const el = trackRef.current
    if (!el) return
    const card = el.querySelector<HTMLElement>('article')
    el.scrollBy({
      behavior: 'smooth',
      left: direction * ((card?.offsetWidth ?? 280) + 12),
    })
  }

  const reviews = data?.reviews ?? []
  // Nothing to show on this platform at all (no sub-ratings collected): the
  // widget stays out of the page. With a facility filter active it stays, so
  // the filter can be undone.
  if (!data || (reviews.length === 0 && facilityIds.length === 0)) return null

  const thumbWidth = Math.min(100, scroll.ratio * 100)
  const thumbLeft =
    scroll.max > 0 ? (scroll.left / scroll.max) * (100 - thumbWidth) : 0

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>Guest Reviews Overview</h3>
          <p className={styles.subtitle}>
            <FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} />
            This overview includes only reviews that provide detailed feedback
            across specific criteria
          </p>
        </div>
        {facilities.length > 1 && (
          <ScopeSelect
            allLabel="All Facilities"
            noun="facilities"
            onChange={setFacilityIds}
            options={facilities}
            selected={facilityIds}
          />
        )}
      </div>

      {reviews.length === 0 ? (
        <p className={styles.empty}>
          No guest reviews with detailed ratings for the selected facilities.
        </p>
      ) : (
        <>
          <div className={styles.carousel}>
            {scroll.left > 4 && (
              <button
                aria-label="Previous reviews"
                className={`${styles.arrow} ${styles.arrowLeft}`}
                onClick={() => step(-1)}
                type="button"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}
            <div className={styles.track} onScroll={measure} ref={trackRef}>
              {reviews.map((review) => (
                <GuestReviewCard key={review._id} review={review} />
              ))}
            </div>
            {scroll.left < scroll.max - 4 && (
              <button
                aria-label="Next reviews"
                className={`${styles.arrow} ${styles.arrowRight}`}
                onClick={() => step(1)}
                type="button"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            )}
          </div>
          {scroll.max > 0 && (
            <div className={styles.progress}>
              <span
                className={styles.progressThumb}
                style={{ left: `${thumbLeft}%`, width: `${thumbWidth}%` }}
              />
            </div>
          )}
        </>
      )}
    </section>
  )
}
